import { useCallback, useState } from "react";
import useSWR, { mutate, type SWRResponse } from "swr";
import {
  type UserSearchHistoryTitleResponseType,
  type UserSearchHistoryTitleListResponseType,
} from "../types";
import { useApiClient } from "@/hooks/useApiClient";

const TITLE_SEARCH_HISTORY_API_PATH = "/user-search-history/real-estate-title";

export const useTitleSearchHistory = (
  shouldFetch = false
): {
  searchHistoryData: UserSearchHistoryTitleResponseType[];
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<UserSearchHistoryTitleResponseType[]>;
} => {
  const { apiClient } = useApiClient();
  const [isManualLoading, setIsManualLoading] = useState(false);

  const fetcher = useCallback(
    async (url: string): Promise<UserSearchHistoryTitleListResponseType> => {
      const response =
        await apiClient.get<UserSearchHistoryTitleListResponseType>(url);
      return response.data;
    },
    [apiClient]
  );

  const {
    data,
    error,
  }: SWRResponse<UserSearchHistoryTitleListResponseType, Error> = useSWR<
    UserSearchHistoryTitleListResponseType,
    Error
  >(shouldFetch ? TITLE_SEARCH_HISTORY_API_PATH : null, fetcher, {
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
    suspense: false,
    shouldRetryOnError: false,
  });

  // 必要なときに手動でデータを取得するための再取得関数
  const refetch = useCallback(async (): Promise<
    UserSearchHistoryTitleResponseType[]
  > => {
    try {
      setIsManualLoading(true);
      const result = await fetcher(TITLE_SEARCH_HISTORY_API_PATH);
      // 第3引数をfalseにして再検証を防止する
      void mutate("/user-search-history/title", result, false);
      return result.items;
    } catch (e) {
      console.error("検索履歴の取得に失敗しました:", e);
      void mutate("/user-search-history/title", undefined, false);
      throw e;
    } finally {
      setIsManualLoading(false);
    }
  }, [fetcher]);

  // SWRのローディング状態と手動のローディング状態を組み合わせる
  const isLoading = (shouldFetch && !error && !data) || isManualLoading;

  return {
    searchHistoryData: data?.items ?? [],
    totalCount: data?.count ?? 0,
    isLoading,
    isError: !!error,
    refetch,
  };
};
